/**
 * @file IntegrationEditDialog.tsx
 * @description Диалог создания / редактирования интеграции (phase08.5 shadcn).
 *
 * phase19.29: нативные <select> (скоуп, статус) заменены на SelectField —
 * нативный список рисует ОС, тач-цель меньше 48px; поля ключей и настроек
 * вынесены в IntegrationSecretFields, состояние и сохранение — в
 * use-integration-form (диалог — только разметка).
 */

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { IntegrationConfigFields, IntegrationCredentialFields } from "./IntegrationSecretFields";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SelectField, type SelectFieldOption } from "../ui/SelectField";
import type {
  PlatformIntegrationDto,
  PlatformIntegrationKind,
} from "../../lib/api-platform-integrations";
import { useIntegrationForm } from "./use-integration-form";

interface Props {
  open: boolean;
  kind: PlatformIntegrationKind;
  integration: PlatformIntegrationDto | null;
  onClose: () => void;
  onSaved: () => void;
  /** Новая настройка — для этого магазина (phase19.28, вкладка магазина). */
  defaultShopId?: string;
}

/**
 * Статусы в форме. Руками выставляются «Активна» и «На паузе»; «Ошибка»
 * ставит сервер — вариант есть, чтобы поле показывало подпись, а не ключ
 * `ERROR`, но выбрать его нельзя. Отправляемое значение не меняется.
 */
const STATUS_OPTIONS: SelectFieldOption[] = [
  { value: "ACTIVE", label: "Активна" },
  { value: "PAUSED", label: "На паузе" },
  { value: "ERROR", label: "Ошибка", disabled: true },
];

export function IntegrationEditDialog({
  open,
  kind,
  integration,
  onClose,
  onSaved,
  defaultShopId,
}: Props) {
  const {
    meta,
    name,
    setName,
    shopId,
    setShopId,
    scopeOptions,
    credentials,
    setCredentials,
    config,
    setConfig,
    isPrimary,
    setIsPrimary,
    status,
    setStatus,
    saving,
    error,
    onSubmit,
  } = useIntegrationForm(kind, integration, onSaved, defaultShopId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {integration ? `Редактировать ${meta.label}` : `Подключить ${meta.label}`}
          </DialogTitle>
          <DialogDescription>{meta.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="int-scope">Область действия</Label>
            <SelectField
              id="int-scope"
              ariaLabel="Область действия"
              value={shopId}
              onChange={setShopId}
              options={scopeOptions}
            />
            <p className="text-xs text-on-surface-variant">
              «Вся платформа» действует для всех магазинов; настройка конкретного магазина для него важнее.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="int-name">Имя экземпляра</Label>
            <Input
              id="int-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Например: «Рабочий» или «Тестовый»"
            />
          </div>

          <IntegrationCredentialFields
            meta={meta}
            editing={integration !== null}
            value={credentials}
            onChange={setCredentials}
          />
          <IntegrationConfigFields meta={meta} value={config} onChange={setConfig} />

          <label className="flex items-center gap-2 cursor-pointer mt-2">
            <Checkbox
              checked={isPrimary}
              onCheckedChange={(c) => setIsPrimary(c === true)}
            />
            <span className="text-sm text-on-surface">
              {shopId === ""
                ? "Использовать как основную (по умолчанию для всех магазинов)"
                : "Использовать как основную для этого магазина"}
            </span>
          </label>

          {integration ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="int-status">Статус</Label>
              <SelectField
                id="int-status"
                ariaLabel="Статус"
                value={status}
                onChange={(next) => {
                  // Руками — только «Активна» / «Пауза»; ошибку ставит сервер.
                  if (next === "ACTIVE" || next === "PAUSED") setStatus(next);
                }}
                options={STATUS_OPTIONS}
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} loading={saving}>
            {saving ? "Сохраняем…" : integration ? "Сохранить" : "Подключить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
